/**
 * Assessment Results Logic Utilities
 * Centralized business logic for purchase states and price calculations
 */

// Purchase states enum
export const PURCHASE_STATES = {
  CAN_PURCHASE_ALL: 'CAN_PURCHASE_ALL',
  OWNS_LP_ONLY: 'OWNS_LP_ONLY', 
  OWNS_MENTORING_ONLY: 'OWNS_MENTORING_ONLY',
  OWNS_ALL: 'OWNS_ALL',
  HAS_SUBSCRIPTION_ACCESS: 'HAS_SUBSCRIPTION_ACCESS',
  PARTIAL_OWNERSHIP: 'PARTIAL_OWNERSHIP'
};

/**
 * Determines the current purchase state based on user ownership
 * @param {Object} params - Parameters object
 * @param {Array} params.products - Array of product recommendations
 * @param {Object} params.personalLearningPath - Learning path object
 * @param {Object} params.mentoring - Mentoring object
 * @returns {string} Purchase state from PURCHASE_STATES enum
 */
export function getPurchaseState({ products, personalLearningPath, mentoring }) {
  const hasLearningProgramPurchased = personalLearningPath?.is_purchased || personalLearningPath?.is_owned;
  
  const hasMentoringPurchased = mentoring?.available && 
    mentoring?.recommendations?.every(item => item.is_purchased || item.is_owned);
  
  const hasSubscriptionAccess = products?.some(product => 
    product.is_subscription_access && product.ownership_type === 'subscription_all_courses'
  );
  
  const unpurchasedProducts = products?.filter(product => 
    !product.is_purchased && !product.is_owned && product.can_purchase
  ) || [];
  
  // Determine state based on ownership combinations
  if (hasSubscriptionAccess && hasMentoringPurchased) {
    return PURCHASE_STATES.OWNS_ALL;
  }
  
  if (hasLearningProgramPurchased && hasMentoringPurchased) {
    return PURCHASE_STATES.OWNS_ALL;
  }
  
  if (hasSubscriptionAccess && !hasMentoringPurchased && mentoring?.available) {
    return PURCHASE_STATES.HAS_SUBSCRIPTION_ACCESS;
  }
  
  if (hasLearningProgramPurchased && !hasMentoringPurchased && mentoring?.available) {
    return PURCHASE_STATES.OWNS_LP_ONLY;
  }
  
  if (!hasLearningProgramPurchased && hasMentoringPurchased) {
    return PURCHASE_STATES.OWNS_MENTORING_ONLY;
  }
  
  if (unpurchasedProducts.length === 0 && !mentoring?.available) {
    return PURCHASE_STATES.OWNS_ALL;
  }
  
  if (unpurchasedProducts.length > 0 && unpurchasedProducts.length < products.length) {
    return PURCHASE_STATES.PARTIAL_OWNERSHIP;
  }
  
  return PURCHASE_STATES.CAN_PURCHASE_ALL;
}

/**
 * Calculates pricing based on selected options and current ownership
 * @param {Object} params - Parameters object
 * @param {Array} params.products - Array of product recommendations
 * @param {Object} params.personalLearningPath - Learning path object
 * @param {Object} params.mentoring - Mentoring object
 * @param {boolean} params.includeMentoring - Whether mentoring is included
 * @param {Object} params.bundlePricing - Bundle pricing data from API
 * @returns {Object} Price calculation result
 */
export function calculatePrice({ 
  products, 
  personalLearningPath, 
  mentoring, 
  includeMentoring, 
  bundlePricing 
}) {
  // Default return object
  const defaultResult = {
    originalPrice: 0,
    finalPrice: 0,
    directDiscount: 0,
    extraDiscount: 0,
    totalSavings: 0,
    hasDiscounts: false,
    mentoringCount: 0,
    mentoringPrice: 0,
    unpurchasedProducts: []
  };

  if (!bundlePricing?.available) {
    return defaultResult;
  }

  const unpurchasedProducts = products?.filter(product => 
    (!product.is_purchased && !product.is_owned && product.can_purchase) ||
    (product.is_subscription_access && !product.is_direct_purchase && 
     product.ownership_type === 'subscription_all_courses')
  ) || [];

  const availableMentoring = mentoring?.available && mentoring?.recommendations ? 
    mentoring.recommendations : [];
  
  const unpurchasedMentoring = availableMentoring.filter(item => 
    !item.is_purchased && !item.is_owned && item.can_purchase
  );

  let pricing;
  
  const purchaseState = getPurchaseState({ products, personalLearningPath, mentoring });
  
  if (purchaseState === 'OWNS_LP_ONLY' || purchaseState === 'HAS_SUBSCRIPTION_ACCESS') {
    // User already has LP, only show mentoring pricing
    pricing = bundlePricing.mentoring;
  } else if (purchaseState === 'OWNS_MENTORING_ONLY') {
    // User already has mentoring, only show LP pricing
    pricing = bundlePricing.learning_path;
  } else if (includeMentoring && bundlePricing.bundle) {
    // Bundle pricing (LP + Mentoring)
    pricing = bundlePricing.bundle;
  } else {
    // Learning path only pricing
    pricing = bundlePricing.learning_path;
  }

  if (!pricing) {
    return defaultResult;
  }

  // Handle different pricing structures
  let originalPrice, finalPrice, directDiscount, extraDiscount, totalSavings;
  
  if (purchaseState === 'OWNS_LP_ONLY' || purchaseState === 'HAS_SUBSCRIPTION_ACCESS') {
    // For mentoring-only pricing
    originalPrice = pricing.list_price || 0;
    finalPrice = pricing.selling_price || 0;
    directDiscount = pricing.direct_discount || 0;
    extraDiscount = pricing.extra_discount || 0;
    totalSavings = directDiscount + extraDiscount;
  } else {
    // For bundle or LP pricing
    originalPrice = pricing.total_list_price || pricing.list_price || 0;
    finalPrice = pricing.bundle_selling_price || pricing.selling_price || 0;
    directDiscount = pricing.direct_discount || 0;
    extraDiscount = pricing.bundle_extra_discount || pricing.extra_discount || 0;
    totalSavings = pricing.total_savings || (directDiscount + extraDiscount) || 0;
  }

  return {
    originalPrice,
    finalPrice,
    directDiscount,
    extraDiscount,
    totalSavings,
    hasDiscounts: directDiscount > 0 || extraDiscount > 0,
    mentoringCount: includeMentoring ? unpurchasedMentoring.length : 0,
    mentoringPrice: includeMentoring && bundlePricing.mentoring ? 
      bundlePricing.mentoring.selling_price || 0 : 0,
    unpurchasedProducts
  };
}

/**
 * Determines what banner should be shown and its configuration
 * @param {string} purchaseState - Current purchase state
 * @param {Object} pricing - Price calculation result
 * @param {Object} mentoring - Mentoring object
 * @returns {Object} Banner configuration
 */
export function getBannerConfig(purchaseState, pricing, mentoring, includeMentoring) {
  const config = {
    show: false,
    title: '',
    subtitle: '',
    showToggle: false,
    buttonText: '',
    buttonAction: 'buyAll'
  };

  switch (purchaseState) {
    case PURCHASE_STATES.CAN_PURCHASE_ALL:
      config.show = true;
      {
        const canMentor = mentoring?.available && mentoring?.recommendations?.length > 0;
        config.showToggle = canMentor;
        if (canMentor && includeMentoring) {
          config.title = 'Beli Paket Bundling Pembelajaran Lengkap + Mentoring';
          config.subtitle = `${pricing.unpurchasedProducts.length} kursus (untuk akses selamanya) + sesi Mentoring`;
        } else {
          config.title = 'Beli Paket Pembelajaran Lengkap';
          config.subtitle = `${pricing.unpurchasedProducts.length} kursus (untuk akses selamanya)`;
        }
      }
      config.buttonText = 'Beli Semua';
      break;

    case PURCHASE_STATES.OWNS_LP_ONLY:
      config.show = true;
      config.title = 'Upgrade dengan Mentoring Session';
      config.subtitle = `${pricing.mentoringCount} mentoring session tersedia`;
      config.showToggle = false;
      config.buttonText = 'Beli Mentoring';
      config.buttonAction = 'buyMentoring';
      break;

    case PURCHASE_STATES.HAS_SUBSCRIPTION_ACCESS:
      config.show = mentoring?.available && pricing.mentoringCount > 0;
      config.title = 'Tambah Mentoring ke Akses Berlangganan Anda';
      config.subtitle = `${pricing.mentoringCount} mentoring session tersedia`;
      config.showToggle = false;
      config.buttonText = 'Beli Mentoring';
      config.buttonAction = 'buyMentoring';
      break;

    case PURCHASE_STATES.OWNS_MENTORING_ONLY:
      config.show = true;
      config.title = 'Lengkapi dengan Learning Path';
      config.subtitle = `${pricing.unpurchasedProducts.length} kursus tersedia`;
      config.showToggle = false;
      config.buttonText = 'Beli Learning Path';
      config.buttonAction = 'buyLearningPath';
      break;

    case PURCHASE_STATES.PARTIAL_OWNERSHIP:
      config.show = pricing.unpurchasedProducts.length > 0;
      config.title = 'Lengkapi Pembelajaran Anda';
      config.subtitle = `${pricing.unpurchasedProducts.length} kursus belum dimiliki`;
      config.showToggle = mentoring?.available && pricing.mentoringCount > 0;
      config.buttonText = 'Beli Sisa Kursus';
      break;

    case PURCHASE_STATES.OWNS_ALL:
    default:
      config.show = false;
      break;
  }

  return config;
}

/**
 * Validates if mentoring toggle should be enabled
 * @param {string} purchaseState - Current purchase state
 * @param {Object} mentoring - Mentoring object
 * @returns {boolean} Whether toggle should be enabled
 */
export function shouldEnableMentoringToggle(purchaseState, mentoring) {
  // Toggle is only enabled when user can purchase both LP and mentoring
  return purchaseState === PURCHASE_STATES.CAN_PURCHASE_ALL && 
         mentoring?.available && 
         mentoring?.recommendations?.length > 0;
}

/**
 * Gets the initial mentoring toggle state
 * @param {string} purchaseState - Current purchase state
 * @param {Object} mentoring - Mentoring object
 * @returns {boolean} Initial toggle state
 */
export function getInitialMentoringState(purchaseState, mentoring) {
  switch (purchaseState) {
    case PURCHASE_STATES.OWNS_LP_ONLY:
    case PURCHASE_STATES.HAS_SUBSCRIPTION_ACCESS:
      return true; // Force mentoring selection
    case PURCHASE_STATES.OWNS_MENTORING_ONLY:
      return false; // Force LP selection
    default:
      return mentoring?.available && mentoring?.recommendations?.length > 0;
  }
}
